import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Copy, Check, Code, ExternalLink, Monitor, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

export default function EmbedGuide() {
  const [copied, setCopied] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop');
  
  const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;
  
  const embedCodes = {
    iframe: `<iframe 
  src="${baseUrl}/embed" 
  width="100%" 
  height="700" 
  frameborder="0" 
  style="border-radius: 12px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);">
</iframe>`,
    
    button: `<!-- SLP Coach Chat Button -->
<script>
(function() {
  var btn = document.createElement('div');
  btn.innerHTML = '<button id="slp-coach-btn" style="position:fixed;bottom:24px;right:24px;background:#f59e0b;color:#0f172a;border:none;padding:16px 24px;border-radius:50px;font-weight:600;cursor:pointer;box-shadow:0 4px 12px rgba(0,0,0,0.15);z-index:9999;display:flex;align-items:center;gap:8px;font-family:system-ui,sans-serif;"><svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/></svg>Chat with Coach</button>';
  document.body.appendChild(btn);
  
  var modal = document.createElement('div');
  modal.id = 'slp-coach-modal';
  modal.style.cssText = 'display:none;position:fixed;bottom:100px;right:24px;width:400px;height:600px;z-index:10000;border-radius:16px;overflow:hidden;box-shadow:0 25px 50px -12px rgba(0,0,0,0.25);';
  modal.innerHTML = '<iframe src="${baseUrl}/embed" width="100%" height="100%" frameborder="0"></iframe>';
  document.body.appendChild(modal);
  
  document.getElementById('slp-coach-btn').onclick = function() {
    var m = document.getElementById('slp-coach-modal');
    m.style.display = m.style.display === 'none' ? 'block' : 'none';
  };
})();
</script>`,

    squarespace: `<!-- Add this to Settings > Advanced > Code Injection > Footer -->
<script>
(function() {
  // Create chat button - SLP lime green brand color
  var btn = document.createElement('button');
  btn.id = 'slp-coach-btn';
  btn.innerHTML = '💬 Change Coach';
  btn.style.cssText = 'position:fixed;bottom:24px;right:24px;background:#c8e600;color:#0f172a;border:none;padding:16px 24px;border-radius:50px;font-weight:600;cursor:pointer;box-shadow:0 4px 12px rgba(0,0,0,0.15);z-index:9999;font-family:system-ui,sans-serif;font-size:16px;transition:transform 0.2s;';
  document.body.appendChild(btn);
  btn.onmouseover = function() { this.style.transform = 'scale(1.05)'; };
  btn.onmouseout = function() { this.style.transform = 'scale(1)'; };
  
  // Create modal container
  var modal = document.createElement('div');
  modal.id = 'slp-modal';
  modal.style.cssText = 'display:none;position:fixed;bottom:100px;right:24px;width:400px;height:600px;max-width:calc(100vw - 48px);max-height:calc(100vh - 140px);z-index:10000;border-radius:16px;overflow:hidden;box-shadow:0 25px 50px -12px rgba(0,0,0,0.25);background:#fff;';
  modal.innerHTML = '<iframe src="${baseUrl}/embed" width="100%" height="100%" frameborder="0" style="border:none;"></iframe>';
  document.body.appendChild(modal);
  
  // Toggle modal
  btn.onclick = function() {
    modal.style.display = modal.style.display === 'none' ? 'block' : 'none';
    btn.innerHTML = modal.style.display === 'none' ? '💬 Change Coach' : '✕ Close';
  };
})();
</script>`
  };

  const copyCode = (type) => {
    navigator.clipboard.writeText(embedCodes[type]);
    setCopied(type);
    toast.success('Code copied to clipboard');
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">Embed SLP Coach</h1>
          <p className="text-slate-400">Add the change coach to your website in minutes</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Squarespace Section - Primary */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
              <Code className="h-5 w-5 text-slate-900" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Squarespace Integration</h2>
              <p className="text-sm text-slate-500">Recommended for your site</p>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900 mb-3">How to add to Squarespace:</h3>
              <ol className="space-y-3 text-sm text-slate-600">
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-slate-900 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                  <span>Go to your Squarespace dashboard → <strong>Settings</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-slate-900 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                  <span>Click <strong>Advanced</strong> → <strong>Code Injection</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-slate-900 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                  <span>Paste the code below into the <strong>Footer</strong> section</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-slate-900 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">4</span>
                  <span>Click <strong>Save</strong> - the chat button will appear on all pages</span>
                </li>
              </ol>
            </div>
            
            <div className="relative">
              <pre className="p-4 bg-slate-900 text-slate-300 text-xs overflow-x-auto">
                <code>{embedCodes.squarespace}</code>
              </pre>
              <Button
                size="sm"
                onClick={() => copyCode('squarespace')}
                className="absolute top-3 right-3 bg-white text-slate-900 hover:bg-slate-100"
              >
                {copied === 'squarespace' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                <span className="ml-2">{copied === 'squarespace' ? 'Copied!' : 'Copy'}</span>
              </Button>
            </div>
          </div>
        </section>

        {/* Preview */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Preview</h2>
          
          <div className="flex gap-2 mb-4">
            <Button
              variant={previewMode === 'desktop' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPreviewMode('desktop')}
            >
              <Monitor className="h-4 w-4 mr-2" />
              Desktop
            </Button>
            <Button
              variant={previewMode === 'mobile' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPreviewMode('mobile')}
            >
              <Smartphone className="h-4 w-4 mr-2" />
              Mobile
            </Button>
          </div>
          
          <div className={`bg-white rounded-xl border border-slate-200 overflow-hidden ${
            previewMode === 'mobile' ? 'max-w-sm mx-auto' : ''
          }`}>
            <div className="bg-slate-100 px-4 py-2 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 bg-red-400 rounded-full" />
                <div className="w-3 h-3 bg-yellow-400 rounded-full" />
                <div className="w-3 h-3 bg-green-400 rounded-full" />
              </div>
              <div className="flex-1 bg-white rounded px-3 py-1 text-xs text-slate-400">
                slpartners.au
              </div>
            </div>
            <div className="relative h-96 bg-gradient-to-br from-slate-800 to-slate-900 p-8">
              <div className="text-white text-center">
                <p className="text-sm text-slate-400 mb-2">Your website content here</p>
                <h3 className="text-2xl font-bold mb-4">Strategic Leadership Partners</h3>
                <p className="text-slate-400">The chat button appears in the bottom right corner →</p>
              </div>
              
              {/* Mock chat button */}
              <div className="absolute bottom-4 right-4">
                <button className="bg-[#c8e600] text-slate-900 px-6 py-3 rounded-full font-semibold shadow-lg flex items-center gap-2">
                  💬 Change Coach
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Alternative Options */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Other Embed Options</h2>
          
          {/* Full Page iFrame */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Full Page Embed</h3>
                <p className="text-sm text-slate-500">Embed directly into a page section</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => copyCode('iframe')}>
                {copied === 'iframe' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <pre className="p-4 bg-slate-50 text-slate-600 text-xs overflow-x-auto">
              <code>{embedCodes.iframe}</code>
            </pre>
          </div>

          {/* Direct Link */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Direct Link</h3>
                <p className="text-sm text-slate-500">Link directly to the coach</p>
              </div>
              <a
                href={`${baseUrl}/embed`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-amber-600 hover:text-amber-700"
              >
                {baseUrl}/embed
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        {/* Help */}
        <section className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <h3 className="font-semibold text-amber-900 mb-2">Need help?</h3>
          <p className="text-sm text-amber-800">
            If you run into any issues embedding the coach on your site, reach out and we'll help you get set up.
          </p>
        </section>

      </main>
    </div>
  );
}
